import { Badge, cn, type BadgeTone } from '@hrms/ui';
import {
  TRAINING_LEARNER_STATUS_LABELS,
  type TrainingDueOverlay,
  type TrainingLearnerStatus,
} from '@hrms/shared';

export interface LearnerStatusBadgeProps {
  /** From `trainingLearnerView()`; `null` is a cancelled assignment, shown to HR as "Removed". */
  status: TrainingLearnerStatus | null;
  overlay?: TrainingDueOverlay;
  /** Completed after the due date — "Completed late" (HR's wording, PLAN §5). */
  late?: boolean;
  className?: string;
}

/**
 * Local tones rather than `@hrms/ui`'s `statusTone`: that switch leaves `completed` on its `info`
 * fallback for two unrelated callers (see its comment), and the learner vocabulary's
 * `not_completed` is not a key it knows. `accent` is avoided for the same reason `hashTone` avoids
 * it.
 */
const STATUS_TONE: Record<TrainingLearnerStatus, BadgeTone> = {
  not_started: 'neutral',
  in_progress: 'info',
  completed: 'success',
  not_completed: 'warning',
};

/** Overdue is red, due soon amber — always with the word, never colour alone. */
const OVERLAY: Record<'overdue' | 'due_soon', { label: string; tone: BadgeTone }> = {
  overdue: { label: 'Overdue', tone: 'danger' },
  due_soon: { label: 'Due soon', tone: 'warning' },
};

/**
 * The four learner states (PLAN §5, R10) plus the Overdue / Due soon chip. One component so My
 * learning, the course cards, the player and HR's Learners tab can never disagree on a word or a
 * colour. The overlay sits beside the status rather than replacing it: a course is "In progress"
 * AND "Overdue" at the same time.
 */
export function LearnerStatusBadge({
  status,
  overlay = null,
  late = false,
  className,
}: LearnerStatusBadgeProps) {
  const label =
    status === null
      ? 'Removed'
      : status === 'completed' && late
        ? 'Completed late'
        : TRAINING_LEARNER_STATUS_LABELS[status];
  const tone: BadgeTone =
    status === null ? 'neutral' : status === 'completed' && late ? 'warning' : STATUS_TONE[status];
  const chip = overlay ? OVERLAY[overlay] : null;

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1.5', className)}>
      <Badge tone={tone}>{label}</Badge>
      {chip && (
        <Badge tone={chip.tone} className="font-semibold">
          {chip.label}
        </Badge>
      )}
    </span>
  );
}
